import { computed, ComputedRef, reactive } from "vue";
import axios from "axios";

import { TeamInvitation } from "@Modules/teams/Data/Models/TeamInvitationModel";

export type TeamInvitationsApi = {
    invitations: ComputedRef<TeamInvitation[]>;
    loading: ComputedRef<boolean>;
    fetchInvitations: () => Promise<void>;
    acceptInvitation: (id: string) => Promise<void>;
    rejectInvitation: (id: string) => Promise<void>;
    reset: () => void;
    setRouter: (router: Pick<HttpInterface, "get" | "post">) => TeamInvitationsApi;
}

export default function TeamInvitationsApi(): TeamInvitationsApi {
    const state = reactive<{
        invitations: TeamInvitation[];
        loading: boolean;
    }>({
        invitations: [],
        loading: false,
    });

    let router: Pick<HttpInterface, "get" | "post"> = axios;

    const that = {
        invitations: computed(() => state.invitations),
        loading: computed(() => state.loading),

        fetchInvitations: async function (): Promise<void> {
            state.loading = true;
            const response = await router.get("/app/api/invitations/team") as HttpResponse<TeamInvitation[]>;
            state.invitations = response.data;
            state.loading = false;
        },

        acceptInvitation: async function (id: string): Promise<void> {
            const invitation = state.invitations.find(inv => inv.id === id);
            if (!invitation || !invitation.team) {
                throw new Error(`Invitation ${id} not found or has no team`);
            }
            const orgSlug = invitation.organization.slug;
            const teamSlug = invitation.team.slug;
            const response = await router.post(`/app/api/organization/${orgSlug}/team/${teamSlug}/invite/${id}/accept`, {});
            if (response.status < 200 || response.status >= 300) {
                throw new Error(`Could not accept invitation ${id}`);
            }
            state.invitations = state.invitations.filter(inv => inv.id !== id);
        },

        rejectInvitation: async function (id: string): Promise<void> {
            const invitation = state.invitations.find(inv => inv.id === id);
            if (!invitation || !invitation.team) {
                throw new Error(`Invitation ${id} not found or has no team`);
            }
            const orgSlug = invitation.organization.slug;
            const teamSlug = invitation.team.slug;
            const response = await router.post(`/app/api/organization/${orgSlug}/team/${teamSlug}/invite/${id}/reject`, {});
            if (response.status < 200 || response.status >= 300) {
                throw new Error(`Could not reject invitation ${id}`);
            }
            state.invitations = state.invitations.filter(inv => inv.id !== id);
        },

        reset: function (): void {
            state.invitations = [];
            state.loading = false;
        },

        setRouter: function (newRouter: Pick<HttpInterface, "get" | "post">) {
            router = newRouter;
            return that;
        },
    };

    return that;
}
